import { Icon } from '@lmc/ui-kit'
import { reportsConstants } from 'locale/team'
import { useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import EditProfileCard from '../../../../../../components/forms/screens/TeamMemberEditFormV2/EditProfileCard'
import ProfileFieldRow from './ProfileFieldRow'

const Clamp = styled.div<{ $expanded: boolean }>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  -webkit-line-clamp: ${({ $expanded }) => ($expanded ? 'unset' : 2)};
`
const Description = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
  margin: 0 0 16px;
`
const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.color.neutral.color8};
  margin: 0 0 16px;
`
const Label = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
`
const Toggle = styled.button`
  background: none;
  border: none;
  padding: 4px 0 0;
  cursor: pointer;
  color: ${({ theme }) => theme.color.status.primary};
  font-size: 16px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

type Props = {
  assignedNames: string[]
  keyworkerNames: string[]
  serviceUser: string
  t: (k: string, opts?: Record<string, string>) => string
}

const AssignmentCard = ({
  assignedNames,
  keyworkerNames,
  serviceUser,
  t
}: Props) => {
  const [expanded, setExpanded] = useState(false)
  const [canTruncate, setCanTruncate] = useState(false)
  const clampRef = useRef<HTMLDivElement>(null)
  const expandedRef = useRef(expanded)
  expandedRef.current = expanded

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when assignedNames changes to re-check overflow
  useLayoutEffect(() => {
    const el = clampRef.current
    if (!el) {
      return
    }

    // Only measure while collapsed: expanding removes the clamp so
    // scrollHeight === clientHeight, which would wrongly clear canTruncate.
    const check = () => {
      if (!expandedRef.current) {
        setCanTruncate(el.scrollHeight > el.clientHeight)
      }
    }

    const observer = new ResizeObserver(check)
    observer.observe(el)
    check()

    return () => observer.disconnect()
  }, [assignedNames])

  return (
    <EditProfileCard
      title={t(reportsConstants.PROFILE_VIEW_CARD_ASSIGNMENT)}
      testId="profile-view-assignment"
      fullWidth
    >
      <Description>
        {t(reportsConstants.PROFILE_VIEW_ASSIGNMENT_DESC, { serviceUser })}
      </Description>
      <Divider />
      <Label>{t(reportsConstants.PROFILE_VIEW_ASSIGNED_TO)}</Label>
      <Clamp ref={clampRef} $expanded={expanded} data-testid="assigned-to-list">
        {assignedNames.join(', ') || t(reportsConstants.PROFILE_VIEW_NO_INFO)}
      </Clamp>
      {canTruncate && (
        <Toggle type="button" onClick={() => setExpanded((v) => !v)}>
          {expanded
            ? t(reportsConstants.PROFILE_VIEW_SHOW_LESS)
            : t(reportsConstants.PROFILE_VIEW_SHOW_MORE)}
          <Icon name={expanded ? 'UpOutlined' : 'DownOutlined'} />
        </Toggle>
      )}
      <ProfileFieldRow
        label={t(reportsConstants.PROFILE_VIEW_KEYWORKER_FOR)}
        value={
          keyworkerNames.join(', ') ||
          t(reportsConstants.PROFILE_VIEW_NOT_ASSIGNED)
        }
      />
    </EditProfileCard>
  )
}

export default AssignmentCard
